import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BaseComponent } from 'src/app/core/infrastructure/classes/base-component';
import { ItemPriceState } from 'src/app/core/models/history-log';
import { RecommendationsService } from 'src/app/core/services/recommendation.service';
import { decodeStateParam, encodeStateParam } from 'src/app/core/constants/inventory-state-filter.constant';
import { CompanyService } from 'src/app/core/services/company.service';
import { switchMap, tap } from 'rxjs/operators';

export interface ChipDef {
  state: ItemPriceState;
  label: string;
}

export interface GroupDef {
  key: string;
  label: string;
  icon: 'increase' | 'decrease' | 'maintain';
  subtitle: string;
  allStates: ItemPriceState[];
  chips: ChipDef[];
}

@Component({
  selector: 'app-quick-filter-recommendation',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './quick-filter-recommendation-header.component.html',
  styleUrls: ['./quick-filter-recommendation-header.component.scss'],
})
export class QuickFilterRecommendationHeaderComponent extends BaseComponent implements OnInit {
  readonly groups: GroupDef[] = [
    {
      key: 'increase',
      label: 'Price Increase',
      icon: 'increase',
      subtitle: 'Items understocked',
      allStates: [ItemPriceState.STOCK_OUT_HIGH_DEMAND, ItemPriceState.UNDERSTOCK_CRITICAL, ItemPriceState.UNDERSTOCK_MODERATE, ItemPriceState.UNDERSTOCK_MILD],
      chips: [
        { state: ItemPriceState.STOCK_OUT_HIGH_DEMAND, label: '+15%' },
        { state: ItemPriceState.UNDERSTOCK_CRITICAL, label: '+12%' },
        { state: ItemPriceState.UNDERSTOCK_MODERATE, label: '+7%' },
        { state: ItemPriceState.UNDERSTOCK_MILD, label: '+3%' },
      ],
    },
    {
      key: 'decrease',
      label: 'Price Decrease',
      icon: 'decrease',
      subtitle: 'Items overstocked / no demand',
      allStates: [ItemPriceState.OVERSTOCK_HIGH, ItemPriceState.OVERSTOCK_CRITICAL, ItemPriceState.NO_DEMAND],
      chips: [
        { state: ItemPriceState.OVERSTOCK_CRITICAL, label: '-5%' },
        { state: ItemPriceState.OVERSTOCK_HIGH, label: '-2%' },
        { state: ItemPriceState.NO_DEMAND, label: '-5%' },
      ],
    },
    {
      key: 'maintain',
      label: 'Maintain / Review',
      icon: 'maintain',
      subtitle: 'Healthy stock or special cases',
      allStates: [ItemPriceState.STOCK_OUT_RESTOCKING, ItemPriceState.BALANCED, ItemPriceState.DEAD_STOCK, ItemPriceState.MISSING_PRICE],
      chips: [
        { state: ItemPriceState.STOCK_OUT_RESTOCKING, label: '' },
        { state: ItemPriceState.MISSING_PRICE, label: '' },
        { state: ItemPriceState.BALANCED, label: '' },
        { state: ItemPriceState.DEAD_STOCK, label: '' },
      ],
    },
  ];

  countByState: Record<string, number> = {};
  activeFilter: string | null = null;
  loading = true;

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private recommendationsService: RecommendationsService,
    private cdr: ChangeDetectorRef,
    private companyService: CompanyService
  ) {
    super();
  }

  ngOnInit(): void {
    this.activatedRoute.queryParamMap.pipe(this.autoCleanUp()).subscribe((params) => {
      this.activeFilter = params.get('state');
      this.cdr.markForCheck();
    });

    this.companyService.getCurrentCompany().pipe(
      tap(() => {
        this.loading = true;
        this.cdr.markForCheck();
      }),
      switchMap(() => this.recommendationsService.getCountByState()),
      this.autoCleanUp()
    ).subscribe((counts) => {
      this.countByState = counts;
      this.loading = false;
      this.cdr.markForCheck()
    });
  }

  getGroupTotal(group: GroupDef): number {
    return group.allStates.reduce((sum, state) => sum + (this.countByState[state] || 0), 0);
  }

  getChipCount(state: ItemPriceState): number {
    return this.countByState[state] || 0;
  }

  isGroupActive(group: GroupDef): boolean {
    if (!this.activeFilter) return false;
    const activeStates = decodeStateParam(this.activeFilter);
    return group.allStates.every((s) => activeStates.includes(s));
  }

  isChipActive(state: ItemPriceState): boolean {
    if (!this.activeFilter) return false;
    return decodeStateParam(this.activeFilter).includes(state);
  }

  onGroupClick(group: GroupDef): void {
    if (this.isGroupActive(group)) {
      this.navigate(null);
    } else {
      this.navigate(encodeStateParam(group.allStates));
    }
  }

  onChipClick(event: MouseEvent, state: ItemPriceState): void {
    event?.stopPropagation();
    event?.preventDefault();

    if (this.isChipActive(state)) {
      this.navigate(null);
    } else {
      this.navigate(encodeStateParam([state]));
    }
  }

  private navigate(stateParam: string | null): void {
    this.router.navigate(['/dashboard/amz-item-repricing/recommendations'], {
      queryParams: stateParam ? { state: stateParam } : {},
    });
  }
}
